import useFixedTtsSamplePreview from '../hooks/useFixedTtsSamplePreview';
import AudioCreateContent from './MessagePreview/AudioCreateContent';
import useServerTtsVoices from '../hooks/useServerTtsVoices';
import { t as lnT } from '../i18n';
import {useTranslation} from 'react-i18next';
import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import Modal from './Modals/Modal';
import CreditsRequiredModal from './Modals/CreditsRequiredModal';
import { MUTATION, QUERY } from '../gql-operations';
import { GET_FEATURE_FLAG } from '../gql-operations/featureFlags';
import { WHITE } from '../appearance/Colors';
import { evaluateFeatureFlagForUser } from '../util/featureFlagAccess';
import {
    FEATURE_UPGRADE_MODAL_PRESET,
    openFeatureUpgradeModal
} from '../util/featureUpgradeModalEvents';
import { openSettingsModal, SETTINGS_MODAL_TAB } from '../util/settingsModalEvents';
import customAnalytics from '../util/custom-analytics';
import useNotificationSystem from './Notifications/useNotificationSystem';

const TTS_VOICE_STORAGE_KEY = 'luckynote_tts_voice';
const TTS_DEFAULT_STYLE = 'NATURAL';


const modalStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        color: WHITE,
        maxWidth: 480,
        width: '92vw',
        borderRadius: 18,
        border: 'none',
        background: '#222729',
        padding: 0,
        overflow: 'hidden'
    },
    overlay: {
        // Open above the workspace (2300) and standalone article reader (2400).
        zIndex: 2500
    }
};

const getStoredTtsVoice = () => {
    if (typeof window === 'undefined') return '';
    const saved = window.localStorage.getItem(TTS_VOICE_STORAGE_KEY);
    return saved?.startsWith('server:') ? saved : '';
};

const parseCreditsFromError = messageText => {
    const requiredMatch = String(messageText || '').match(/Required:\s*([0-9]+(?:\.[0-9]+)?)/i);
    const required = requiredMatch ? Number(requiredMatch[1]) : null;
    return { required: Number.isFinite(required) ? required : null };
};

const getIsServerBackedMessage = message => (
    Boolean(message?.id) &&
    !String(message.id).startsWith('temp-') &&
    !String(message.id).startsWith('x-')
);

function TextToSpeechCreateModal({
    isOpen,
    onClose,
    message,
    source = 'AUTO',
    text = '',
    urlMetaIndex
}) {
    const {t: lnT} = useTranslation();

    const { showDefaultNotification } = useNotificationSystem();
    const [textToSpeechMutation] = useMutation(MUTATION.TEXT_TO_SPEECH);
    const { data: meData } = useQuery(QUERY.ME, { errorPolicy: 'all', skip: !isOpen });
    const { data: textToSpeechFlagData, loading: textToSpeechFlagLoading } = useQuery(GET_FEATURE_FLAG, {
        variables: { name: 'text_to_speech' },
        errorPolicy: 'all',
        skip: !isOpen
    });
    const [selectedTtsVoice, setSelectedTtsVoice] = React.useState(getStoredTtsVoice);
    const serverVoices = useServerTtsVoices(isOpen, selectedTtsVoice, setSelectedTtsVoice, TTS_VOICE_STORAGE_KEY);
    const [isGeneratingAudio, setIsGeneratingAudio] = React.useState(false);
    const [showCreditsRequiredModal, setShowCreditsRequiredModal] = React.useState(false);
    const [creditsRequiredDescription, setCreditsRequiredDescription] = React.useState(
        'This action uses Lucky Credits. Add Lucky Credits to continue.'
    );

    const accessDecision = React.useMemo(
        () => evaluateFeatureFlagForUser(textToSpeechFlagData?.getFeatureFlag, meData?.me, { loading: textToSpeechFlagLoading }),
        [lnT, textToSpeechFlagData?.getFeatureFlag, meData?.me, textToSpeechFlagLoading]
    );

    const {audioRef: voicePreviewAudioRef, playing: isVoicePreviewPlaying,
        loading: isPreviewingVoice, play: playVoiceSample, toggle: handlePreviewVoiceToggle,
        stop: cleanupVoicePreview, setPlaying: setIsVoicePreviewPlaying} = useFixedTtsSamplePreview({
        open: isOpen, voice: selectedTtsVoice,
        onError: () => showDefaultNotification(message?.id, 'Failed to preview voice.')
    });

    const closeModal = React.useCallback(() => {
        if (isGeneratingAudio) {
            return;
        }
        cleanupVoicePreview();
        onClose?.();
    }, [lnT, cleanupVoicePreview, isGeneratingAudio, isPreviewingVoice, onClose]);

    const checkAccess = React.useCallback(() => {
        if (accessDecision?.allowed) {
            return true;
        }
        if (accessDecision?.requiresUpgrade) {
            openFeatureUpgradeModal({
                preset: FEATURE_UPGRADE_MODAL_PRESET.TEXT_TO_SPEECH,
                upgradeType: 'personal'
            });
            return false;
        }
        if (accessDecision?.requiresTeamUpgrade) {
            openFeatureUpgradeModal({
                preset: FEATURE_UPGRADE_MODAL_PRESET.TEXT_TO_SPEECH,
                upgradeType: 'team'
            });
            return false;
        }
        const fallbackMessage = accessDecision?.reason === 'LOADING'
            ? 'Checking audio generation availability. Please try again.'
            : 'Audio generation is currently unavailable.';
        showDefaultNotification(message?.id, fallbackMessage);
        return false;
    }, [lnT, accessDecision, message?.id, showDefaultNotification]);

    const handleGenerate = async () => {
        const normalizedText = String(text || '').trim();
        if (!getIsServerBackedMessage(message) || message?.deletedAt) return;
        if (!normalizedText) {
            showDefaultNotification(message.id, source === 'ARTICLE' ? 'Extract the article first to generate audio.' : 'Add text first to generate audio.');
            return;
        }
        if (!serverVoices.valid || !checkAccess()) return;
        cleanupVoicePreview();

        setIsGeneratingAudio(true);
        try {
            const response = await textToSpeechMutation({
                variables: {
                    data: {
                        messageId: message.id,
                        source,
                        ...(Number.isInteger(urlMetaIndex) ? { urlMetaIndex } : {}),
                        voice: String(selectedTtsVoice || 'alloy').toLowerCase(),
                        style: TTS_DEFAULT_STYLE
                    }
                }
            });
            const result = response?.data?.textToSpeech;
            if (result?.__typename === 'TextToSpeechError') {
                const errorMessage = result.message || lnT('messages:failedToGenerateAudio');
                if (errorMessage.includes('Insufficient Credits')) {
                    const { required } = parseCreditsFromError(errorMessage);
                    setCreditsRequiredDescription(
                        required !== null
                            ? `This action needs ${required.toFixed(2)} Lucky Credits.`
                            : 'This action uses Lucky Credits. Add Lucky Credits to continue.'
                    );
                    setShowCreditsRequiredModal(true);
                } else {
                    showDefaultNotification(message.id, errorMessage.includes('Not enough AI usage') ? lnT('common:insufficientCreditsPleaseAddCreditsToContinue') : errorMessage);
                }
                return;
            }
            if (result?.id) {
                window.dispatchEvent(new CustomEvent('messageUpdated', { detail: { message: result } }));
            }
            showDefaultNotification(message.id, result?.message || 'Audio generation started in the background.');
            customAnalytics.ui_action({
                action: source === 'ARTICLE' ? 'Generate Audio from Article' : 'Generate Audio',
                screen: 'MessageScreen'
            });
            onClose?.();
        } catch (error) {
            const errorMessage = error?.graphQLErrors?.[0]?.message || error?.message || lnT('messages:failedToGenerateAudio');
            showDefaultNotification(message.id, errorMessage.includes('Not enough AI usage') ? lnT('common:insufficientCreditsPleaseAddCreditsToContinue') : errorMessage);
        } finally {
            setIsGeneratingAudio(false);
        }
    };

    const excerpt = React.useMemo(() => {
        const normalized = String(text || '').replace(/\s+/g, ' ').trim();
        return normalized.length > 340 ? `${normalized.slice(0, 340).trim()}...` : normalized;
    }, [lnT, text]);

    return (
        <>
            <Modal isOpen={isOpen} onRequestClose={closeModal} style={modalStyles} contentLabel={lnT('messages:createAudio')}>
                <AudioCreateContent excerpt={excerpt} voices={serverVoices.voices}
                    voicesLoading={serverVoices.loading} voicesError={serverVoices.error}
                    voice={selectedTtsVoice} playing={isVoicePreviewPlaying} previewing={isPreviewingVoice}
                    generating={isGeneratingAudio} canContinue={Boolean(excerpt) && serverVoices.valid}
                    onContinue={handleGenerate} onClose={closeModal} onPreviewToggle={handlePreviewVoiceToggle}
                    onVoiceChange={nextVoice => {
                        cleanupVoicePreview(); setSelectedTtsVoice(nextVoice);
                        window.localStorage.setItem(TTS_VOICE_STORAGE_KEY, nextVoice);
                        playVoiceSample(nextVoice);
                    }}>
                    <audio ref={voicePreviewAudioRef} preload="none" style={{display: 'none'}}
                        onPlay={() => setIsVoicePreviewPlaying(true)} onPause={() => setIsVoicePreviewPlaying(false)}
                        onEnded={() => setIsVoicePreviewPlaying(false)}/>
                </AudioCreateContent>
            </Modal>
            <CreditsRequiredModal
                isOpen={showCreditsRequiredModal}
                description={creditsRequiredDescription}
                onRequestClose={() => {
                    setShowCreditsRequiredModal(false);
                    setCreditsRequiredDescription('This action uses Lucky Credits. Add Lucky Credits to continue.');
                }}
                onCheckout={() => {
                    setShowCreditsRequiredModal(false);
                    setCreditsRequiredDescription('This action uses Lucky Credits. Add Lucky Credits to continue.');
                    openSettingsModal(SETTINGS_MODAL_TAB.UPGRADES);
                }}
            />
        </>
    );
}

export default TextToSpeechCreateModal;
